import { SpaCareReturn } from '../components/SpaCareReturn';
import { useEffect } from 'react';
import { ArrowRightIcon, ExternalLinkIcon } from 'lucide-react';
import { business, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Build Your Replacement Spa Cover | Pegasus Spas';
const DESCRIPTION = 'Build and order a custom-fit premium replacement hot tub cover from Pegasus Spas in Brooklyn, Michigan, with local help on measurements and cutouts.';
const CANONICAL = 'https://www.pegasus-spas.com/replacementcovers-premium.html';
const BUILDER_URL = 'https://www.pegasus-spas.com/replacementcovers-premium.html';

/**
 * Vertical offset used to mask the legacy live-site header/navigation at the top
 * of the embedded builder so its content begins at "Order Your Premium Spa Cover".
 */
const MASK_MOBILE = 315;
const MASK_DESKTOP = 150;
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function CoversPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1172" data-mp="2tuj6uqmnqc1v">
      <SpaCareReturn data-id="element-1173" data-mp="2ttzqhecnnvpy" />
      {/* Intro band */}
      <section aria-labelledby="covers-h1" className="w-full bg-water" data-id="element-1174" data-mp="2owq5cawau67x">
        <div className="mx-auto max-w-container px-5 py-8 sm:px-8 lg:py-10" data-id="element-1175" data-mp="2oykdspqdw5v4">
          <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1176" data-mp="1ihyyzroept6i">
            Replacement Spa Covers
          </p>
          <h1 id="covers-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-1177" data-mp="1djf8ddyvbt7t">
            Build Your Replacement Cover
          </h1>
          <p className="mt-3 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-1178" data-mp="9h4hu2sqs00f">
            Answer a few questions below and we&rsquo;ll help you order a custom-fit replacement cover for
            your spa. Not sure about a measurement or a cutout? Call or text {business.phone} and
            we&rsquo;ll walk through it with you.
          </p>
        </div>
      </section>

      {/* Cover builder */}
      <section aria-labelledby="builder-heading" className="w-full bg-white" data-id="element-1179" data-mp="38ykpy6mcano7">
        <div className="mx-auto max-w-container px-5 pb-12 pt-8 sm:px-8 lg:pb-16" data-id="element-1180" data-mp="38whpstrh3e5r">
          <h2 id="builder-heading" className="sr-only" data-id="element-1181" data-mp="3dxx9oihwzemk">
            Replacement cover builder
          </h2>

          <div className="relative h-[1500px] w-full overflow-hidden rounded-card border border-ink/15 bg-white sm:h-[1700px] lg:h-[1900px]" data-id="element-1182" data-mp="2z2hnt0rhmp19">
            <iframe title="Pegasus Spas replacement cover builder" src={BUILDER_URL} className="absolute left-0 w-full border-0" style={{
            top: `-${MASK_MOBILE}px`,
            height: `calc(100% + ${MASK_MOBILE}px)`
          }} loading="lazy" data-id="element-1183" data-mp="2kd67s9sdam0i" />
            <style data-id="element-1184" data-mp="2pbhj6oyb94ku">{`
              @media (min-width: 640px) {
                iframe[title="Pegasus Spas replacement cover builder"] {
                  top: -${MASK_DESKTOP}px !important;
                  height: calc(100% + ${MASK_DESKTOP}px) !important;
                }
              }
            `}</style>
          </div>

          <p className="mt-3 text-[16px] leading-6 text-ink/75" data-id="element-1185" data-mp="383k266yfcwy9">
            <a href={BUILDER_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1186" data-mp="384233gw17zci">
              Open the cover builder in a new tab
              <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-1187" data-mp="1p4m1xb9jnf34" />
            </a>
          </p>
        </div>
      </section>

      {/* Related actions */}
      <section aria-label="Related actions" className="w-full border-t border-ink/10 bg-water" data-id="element-1188" data-mp="1u35tpsatjl3e">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-1189" data-mp="whhwj56jgyqt">
          <ul className="flex flex-col gap-x-10 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1190" data-mp="11e8bfp6lxoan">
            <li data-id="element-1191" data-mp="16duwulwlaauj">
              <SiteLink to={routes.coverLifts} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1192" data-mp="16dbh9buevyjo">
                Need a Cover Lifter?
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1193" data-mp="b3zi595gr5z6" />
              </SiteLink>
            </li>
            <li data-id="element-1194" data-mp="6794stl1mohg">
              <SiteLink to={routes.contact} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1195" data-mp="66psbse5wsat">
                Need Help With Measurements or Special Cutouts?
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1196" data-mp="3jn726zk72wp9" />
              </SiteLink>
            </li>
          </ul>
        </div>
      </section>
    </main>;
}